import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../api/client';

// Operators that target the user's team membership instead of a field value.
// When one of these is selected the value editor swaps from a free text input
// to a multi-select of the tenant's teams. Stored value is an array of team
// NAMES (not UUIDs) so rules are portable across dev/qa/prod.
const TEAM_OPERATORS = new Set(['user_in_teams', 'user_not_in_teams']);
const NO_VALUE_OPERATORS = new Set(['not_empty', 'empty']);

/**
 * Shared visibility rules editor.
 *
 * Extracted from a local function inside ScreenBuilder.js so FieldManager can
 * reuse it when editing field_definitions.visibility_rules.
 *
 * Rule shape:
 *   { operator: 'AND'|'OR', conditions: [{ <fieldKey>, op, value }] }
 *
 * There are two places visibility rules live in the system, and they use
 * different keys to reference the "which field to test" part:
 *
 *   1. Screen widgets (stored in screen layout JSONB, evaluated by the
 *      client-side ScreenRenderer). These use `field_id` — the field
 *      definition UUID — because widgets need a stable pointer even if
 *      a field is renamed.
 *   2. Field definitions (stored in `field_definitions.visibility_rules`,
 *      evaluated server-side by api/utils/fieldVisibility.js). These use
 *      `field_name` — the human field name — because the server-side
 *      evaluator works off the named field_data map.
 *
 * The `fieldKey` prop selects which format this editor produces:
 *   fieldKey="field_id"   → ScreenBuilder mode (default)
 *   fieldKey="field_name" → FieldManager mode
 *
 * The fields prop expects [{ id, name, label }] either way.
 *
 * Props:
 *   rules     — current rule object or null/undefined for "no rule"
 *   fields    — available fields to reference in conditions
 *   onChange  — (newRules | null) => void  (null means "remove rule entirely")
 *   fieldKey  — 'field_id' (default) | 'field_name'
 */
export default function VisibilityRulesEditor({ rules, fields, onChange, fieldKey = 'field_id' }) {
  const currentRules = rules || { operator: 'AND', conditions: [] };

  // Tenant teams — loaded once when the editor mounts. Used by the team-aware
  // operators (user_in_teams / user_not_in_teams) to render a multi-select.
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTeamsLoading(true);
    api.get('/teams')
      .then((r) => {
        if (cancelled) return;
        setTeams(Array.isArray(r.data?.teams) ? r.data.teams : []);
        setTeamsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setTeamsError(err?.response?.data?.error || err.message || 'Failed to load teams');
      })
      .finally(() => {
        if (!cancelled) setTeamsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const addCondition = () => {
    onChange({
      ...currentRules,
      conditions: [...currentRules.conditions, { [fieldKey]: '', op: 'not_empty', value: '' }],
    });
  };

  const updateCondition = (index, updates) => {
    const conditions = [...currentRules.conditions];
    conditions[index] = { ...conditions[index], ...updates };
    onChange({ ...currentRules, conditions });
  };

  // Switching to a team operator clears the previous value (string → array)
  // and clears field selection (team operators don't reference a field).
  // Switching back from a team operator clears the value array.
  const changeOperator = (index, newOp) => {
    const wasTeam = TEAM_OPERATORS.has(currentRules.conditions[index]?.op);
    const isTeam = TEAM_OPERATORS.has(newOp);
    const updates = { op: newOp };
    if (wasTeam !== isTeam) {
      updates.value = isTeam ? [] : '';
    }
    if (isTeam) {
      // Team operators don't bind to a field — null out the field selector.
      updates[fieldKey] = '';
    }
    updateCondition(index, updates);
  };

  const toggleTeamInValue = (index, teamName) => {
    const cur = currentRules.conditions[index];
    const arr = Array.isArray(cur.value) ? cur.value : [];
    const next = arr.includes(teamName)
      ? arr.filter((n) => n !== teamName)
      : [...arr, teamName];
    updateCondition(index, { value: next });
  };

  const removeCondition = (index) => {
    const conditions = currentRules.conditions.filter((_, i) => i !== index);
    onChange(conditions.length > 0 ? { ...currentRules, conditions } : null);
  };

  // Which attribute of `fields` becomes the `<option value>` depends on fieldKey.
  const optionValueOf = (f) => (fieldKey === 'field_name' ? f.name : f.id);

  return (
    <div className="border-t border-gray-100 pt-4">
      <label className="block text-xs font-medium text-gray-500 mb-2">Visibility Rules</label>
      {currentRules.conditions.length > 0 && (
        <div className="space-y-2 mb-2">
          {currentRules.conditions.length > 1 && (
            <select
              value={currentRules.operator}
              onChange={(e) => onChange({ ...currentRules, operator: e.target.value })}
              className="text-xs px-2 py-1 border border-gray-200 rounded w-full"
            >
              <option value="AND">ALL conditions must match</option>
              <option value="OR">ANY condition must match</option>
            </select>
          )}
          {currentRules.conditions.map((cond, i) => {
            const isTeamOp = TEAM_OPERATORS.has(cond.op);
            const selectedTeams = Array.isArray(cond.value) ? cond.value : [];
            return (
              <div key={i} className="bg-gray-50 rounded-lg p-2 text-xs space-y-1.5">
                <div className="flex items-center gap-1">
                  {/* Field picker is hidden for team operators — they don't bind to a field */}
                  {!isTeamOp ? (
                    <select
                      value={cond[fieldKey] || ''}
                      onChange={(e) => updateCondition(i, { [fieldKey]: e.target.value })}
                      className="flex-1 px-1.5 py-1 border border-gray-200 rounded text-xs"
                    >
                      <option value="">Field...</option>
                      {[...fields]
                        .sort((a, b) =>
                          (a.label || a.name || '').localeCompare(b.label || b.name || '', undefined, { sensitivity: 'base' })
                        )
                        .map((f) => (
                          <option key={f.id} value={optionValueOf(f)}>
                            {f.label}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <div className="flex-1 px-1.5 py-1 text-xs text-gray-500 italic">
                      (no field — applies to current user)
                    </div>
                  )}
                  <button
                    onClick={() => removeCondition(i)}
                    className="text-gray-400 hover:text-red-500 shrink-0"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <select
                    value={cond.op}
                    onChange={(e) => changeOperator(i, e.target.value)}
                    className="px-1.5 py-1 border border-gray-200 rounded text-xs"
                  >
                    <option value="equals">=</option>
                    <option value="not_equals">!=</option>
                    <option value="not_empty">has value</option>
                    <option value="empty">is empty</option>
                    <option value="contains">contains</option>
                    <option value="array_includes">array includes</option>
                    <option value="array_excludes">array excludes</option>
                    <option value="user_in_teams">User is in team(s)</option>
                    <option value="user_not_in_teams">User is NOT in team(s)</option>
                  </select>
                  {!NO_VALUE_OPERATORS.has(cond.op) && !isTeamOp && (
                    <input
                      type="text"
                      value={cond.value || ''}
                      onChange={(e) => updateCondition(i, { value: e.target.value })}
                      placeholder="value"
                      className="flex-1 px-1.5 py-1 border border-gray-200 rounded text-xs"
                    />
                  )}
                </div>
                {isTeamOp && (
                  <div className="border border-gray-200 rounded bg-white p-1.5 space-y-1">
                    {teamsLoading && <div className="text-gray-400 italic">Loading teams…</div>}
                    {teamsError && <div className="text-red-500">{teamsError}</div>}
                    {!teamsLoading && !teamsError && teams.length === 0 && (
                      <div className="text-gray-400 italic">No teams in this tenant yet.</div>
                    )}
                    {!teamsLoading && !teamsError && teams.map((t) => (
                      <label key={t.id} className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={selectedTeams.includes(t.name)}
                          onChange={() => toggleTeamInValue(i, t.name)}
                          className="h-3 w-3"
                        />
                        <span className="text-xs">{t.name}</span>
                        {t.is_default && <span className="text-[10px] text-gray-400">(default)</span>}
                      </label>
                    ))}
                    <div className="text-[10px] text-gray-500 pt-1 border-t border-gray-100">
                      Use to restrict field visibility to specific teams (e.g. settlement amount only visible to Pre-Litigation, Settlement, Trial teams). Tenant admins always see all fields regardless of team.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <button
        onClick={() => addCondition()}
        className="text-xs text-brand-600 hover:text-brand-700 font-medium"
      >
        + Add visibility rule
      </button>
    </div>
  );
}
